import { useCallback, useEffect, useRef, useState } from 'react';

import { asApiError, type PSErrorClass } from '@/src/api/apiError';
import membershipsApi, { type Membership } from '@/src/api/membershipsApi';

/**
 * The plan catalog, fetched once on mount. Public, so it runs signed out —
 * which is the state the upsell is loudest in.
 *
 * No `retry`, unlike [[useHomePageEvents]]: `refresh` IS the retry here. It
 * clears the error before it asks again, so a call site with no plans yet drops
 * back to `isLoading` (bones, not a stale failure) while one that already has
 * them keeps drawing — the two waits [[useHomePageEvents]] needs two functions
 * for, off one.
 *
 * It never toasts — a failure comes out as `error` and the view decides whether
 * it has anywhere to draw it. Home's upsell simply doesn't draw; the Access Pass
 * landing page, where the plans ARE the page, draws the failure and offers the
 * refresh.
 */

export type MembershipPlansState = {
  /** Non-null iff the fetch succeeded. `[]` is a real answer — an empty catalog. */
  plans: Membership[] | null;
  isLoading: boolean;
  error: PSErrorClass | null;
  /** Re-runs the fetch. Awaitable, so pull-to-refresh can wait on it. */
  refresh: () => Promise<void>;
};

export function useMembershipPlans(): MembershipPlansState {
  const [plans, setPlans] = useState<Membership[] | null>(null);
  const [error, setError] = useState<PSErrorClass | null>(null);
  // Cleared on unmount so neither the mount fetch nor a refresh still in the air
  // writes state after the screen has gone.
  const isMounted = useRef(true);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await membershipsApi.getAllMemberships();
      if (isMounted.current) setPlans(data);
    } catch (cause: unknown) {
      if (isMounted.current) setError(asApiError(cause));
    }
  }, []);

  useEffect(() => {
    isMounted.current = true;
    // False positive: `load`'s only synchronous write clears an error that is
    // already null on mount, and the rest lands after awaiting the request.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load();

    return () => {
      isMounted.current = false;
    };
  }, [load]);

  return {
    plans,
    error,
    isLoading: plans === null && error === null,
    refresh: load,
  };
}
